<template>
  <div v-if="data" class="relative h-full pb-4 overflow-x-auto bg-white">
    <table v-if="data" class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <!-- Recipient Information -->
        <BaseDetailsTableRow
          :name="$t('representative.recipientCode')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="data.companyRecipient"
            :mode="mode"
            v-model="data.companyRecipient.code"
            color="base"
            flat
            :warning="getFieldSoftError(['representativeRecipientId', 'companyRecipientId'])"
            show-error-as-tooltip
            @click="isRecipientModalOpen = true" />
          <BaseInput
            v-else
            :mode="mode"
            color="base"
            :disabled="!data.representativeRecipientCode"
            flat
            :warning="getFieldSoftError(['representativeRecipientCode'])"
            show-error-as-tooltip
            @click="isRecipientModalOpen = true" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.recipientRemotedCode')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="'view'"
            v-model="data.representativeRecipientCode"
            color="base"
            flat
            :warning="getFieldSoftError(['representativeRecipientCode'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.recipientRemotedTaxCode')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="'view'"
            v-model="data.representativeRecipientTaxCode"
            color="base"
            flat
            :warning="getFieldSoftError(['representativeRecipientTaxCode'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.recipientRemotedName')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="'view'"
            v-model="data.representativeRecipientName"
            color="base"
            flat
            :warning="getFieldSoftError(['representativeRecipientName'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.recipientRemotedAddress')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="'view'"
            v-model="data.representativeRecipientAddress"
            color="base"
            flat
            :warning="getFieldSoftError(['representativeRecipientAddress'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.recipientRemotedCity')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="'view'"
            v-model="data.representativeRecipientCity"
            color="base"
            flat
            :warning="getFieldSoftError(['representativeRecipientCity'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <!-- Recipient Site Information -->

        <BaseDetailsTableRow
          :name="$t('representative.recipientSiteCode')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.recipientSiteCode"
            color="base"
            flat
            :warning="getFieldSoftError(['recipientSiteCode'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.recipientSiteName')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.recipientSiteName"
            color="base"
            flat
            :warning="getFieldSoftError(['recipientSiteName'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.recipientSiteAddress')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.recipientSiteAddress"
            color="base"
            flat
            :warning="getFieldSoftError(['recipientSiteAddress'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.recipientSiteCity')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.recipientSiteCity"
            color="base"
            flat
            :warning="getFieldSoftError(['recipientSiteCity'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>
      </tbody>
    </table>

    <DCCustomerModalCreateEdit
      v-if="data.representativeRecipient && isRecipientModalOpen"
      :customer="data.representativeRecipient"
      :representative-id="data.representativeId!"
      :existing-customers="[]"
      mode="assign"
      @save="onRecipientSave"
      @close-modal="isRecipientModalOpen = false" />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import BaseDetailsTableRow from '@/components/common/BaseDetailsTableRow.vue';
import BaseInput from '../common/BaseInput.vue';
import DCCustomerModalCreateEdit from '@/components/traders/DCCustomerModalCreateEdit.vue';
import type { DCCustomer, DCDocumentHeader } from '@/types/DataCollection';
import { ref, onMounted } from 'vue';
import { getDCCustomersWithConditions } from '@/services/api/dataCollection';

const { t: $t } = useI18n();

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout?: boolean;
  validations?: any;
}>();

const data = defineModel<Partial<DCDocumentHeader> | null>();

const availableRecipients = ref<DCCustomer[]>([]);
const isRecipientModalOpen = ref(false);

onMounted(() => {
  if (data.value?.representativeId) {
    loadAvailableRecipients(data.value.representativeId);
  }
});

async function loadAvailableRecipients(representativeId: string) {
  try {
    const response = await getDCCustomersWithConditions({ representativeId });
    availableRecipients.value = response.data || [];
  } catch (error) {
    console.error('Failed to load available recipients:', error);
    availableRecipients.value = [];
  }
}

function onRecipientSave(recipient: DCCustomer) {
  if (!data.value) return;
  data.value.representativeRecipient = recipient;
  data.value.companyRecipientId = recipient.customerId;
  data.value.companyRecipient = recipient.customer;
  isRecipientModalOpen.value = false;
}

function getFieldSoftError(fieldNames: string[]): string {
  if (!props.validations || props.mode === 'view') return '';

  for (const fieldName of fieldNames) {
    const validationField = props.validations[fieldName];
    if (validationField?.dynamicRequired?.$invalid) {
      return validationField.dynamicRequired.$message;
    }
  }

  return '';
}
</script>
